import { addDays, endOfDay, isBefore, isSameDay } from 'date-fns';
import { useUpdateTask } from 'features/tasks';
import { useUpdateUserSettings } from 'features/user';
import { useCallback, useMemo } from 'react';
import { halfHour } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { TaskCardTimeSection } from 'shared/types/orderable-section';
import { DayPartSections } from 'shared/types/page-sections';
import { SortingMode } from 'shared/types/sorting-mode';
import { TaskType } from 'shared/types/task-base';
import { TaskCardTask } from 'shared/types/task-card-task';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { isNumber } from 'shared/utils/is-number';
import { createPlannedOccurrenceForDate } from 'shared/utils/schedules';

export const useHandleTimeSectionDrop = (sections: TaskCardTimeSection[]) => {
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const { submit: updateTask } = useUpdateTask();
  const updateSettings = useUpdateUserSettings();

  const oldSections = useMemo(() => structuredClone(sections), [sections]);

  const updateTimeslotOnTask = useCallback(
    (task: TaskCardTask, startTime?: number | null) => {
      // Repeating/habit
      if (task.raw.task.schedules?.length) {
        const planning = getDueTaskPlanning(task.raw.task, {
          date: today,
          today,
          weekStartsOn,
        });
        const plannedToday = planning.find((plannedOccurrence) =>
          isSameDay(plannedOccurrence.date, today),
        );
        const overduePlannedOccurrence = plannedToday
          ? undefined
          : planning.find((plannedOccurrence) =>
              isBefore(plannedOccurrence.date, today),
            );

        const overdueDate = overduePlannedOccurrence?.date;

        const timeslot = isNumber(startTime)
          ? {
              startTime,
              duration: plannedToday
                ? plannedToday.timeslot?.duration ?? halfHour
                : overduePlannedOccurrence?.timeslot?.duration ?? halfHour,
            }
          : null;

        updateTask({
          id: task.id,
          schedules: createPlannedOccurrenceForDate(
            task.raw.task.schedules,
            today,
            {
              timeslot,
              weekStartsOn,
              originalDate: overdueDate ?? today,
            },
          ),
        });

        return;
      }

      // Task
      const timeslot = isNumber(startTime)
        ? { startTime, duration: task.raw.task.timeslot?.duration ?? halfHour }
        : null;

      updateTask({
        id: task.id,
        endStrategy: {
          deadline: endOfDay(new Date()),
          completionCount: null,
        },
        timeslot,
      });
    },
    [today, updateTask, weekStartsOn],
  );

  const updateTaskToOverdue = useCallback(
    (task: TaskCardTask) => {
      if (task.raw.task.type !== TaskType.Task) {
        return;
      }
      updateTask({
        id: task.id,
        endStrategy: {
          deadline: endOfDay(addDays(new Date(), -1)),
          completionCount: null,
        },
      });
    },
    [updateTask],
  );

  return useCallback(
    (newSections: TaskCardTimeSection[], movedTask: TaskCardTask) => {
      const newSection = newSections.find(({ items }) =>
        items.find(({ id }) => id === movedTask.id),
      );

      const oldSection = oldSections.find(({ items }) =>
        items.find(({ id }) => id === movedTask.id),
      );

      if (!newSection || !oldSection) {
        // data error
        return;
      }

      if (
        newSection.id !== oldSection.id &&
        newSection.id !== DayPartSections.Overdue
      ) {
        updateTimeslotOnTask(movedTask, newSection.startTime);
      }
      if (
        newSection.id !== oldSection.id &&
        newSection.id === DayPartSections.Overdue
      ) {
        updateTaskToOverdue(movedTask);
      }

      updateSettings({
        todoSortingMode: SortingMode.Custom,
        todayGroupSorting: {
          dayPart: {
            sorting: {
              [oldSection.id as DayPartSections]: oldSection.items.map(
                ({ id }) => id,
              ),
              [newSection.id as DayPartSections]: newSection.items.map(
                ({ id }) => id,
              ),
            },
          },
        },
      });
    },
    [oldSections, updateSettings, updateTaskToOverdue, updateTimeslotOnTask],
  );
};
